import { alpha, useTheme } from '@mui/material/styles';
import React, { forwardRef, useMemo } from 'react';
import { InviteStatus } from 'src/provider/VoiceChannel';
import styled, { css, keyframes } from 'styled-components';
import {
  CPAvatarSize,
  avatarSizeBorder,
} from '../../base/CPAvatar/avatarSizes';
import { CustomAvatarProps } from '../../custom-avatar';

const bounce = (color: string, size: CPAvatarSize) => keyframes`
    0%, 100% {
        box-shadow: 0 0 0 0 ${color};
    }
    50%, 75% {
        box-shadow: 0 0 1px ${avatarSizeBorder[size] * 1.5}px ${color};
    }
    57% {
        box-shadow: 0 0 1px ${avatarSizeBorder[size] * 0.8}px ${color};
    }
`;

const ContainerStyled = styled('div')<{
  $conditions: { calling: boolean; color: string; size: CPAvatarSize };
}>(
  ({ $conditions: { calling, color, size } }) =>
    calling &&
    css`
      & > * {
        animation: ${bounce(color, size)} 2s cubic-bezier(0.65, 0, 0.45, 1) 3s infinite;
      }
    `,
);

interface Props extends CustomAvatarProps {
  children: React.ReactNode;
  inviteStatus: InviteStatus | undefined;
  size: CPAvatarSize;
}

export const CallingAvatar = forwardRef<HTMLDivElement, Props>(
  ({ children, inviteStatus, size }, ref) => {
    const theme = useTheme();

    const isCalling = useMemo(
      () => inviteStatus === 'calling' || inviteStatus === 'ringing',
      [inviteStatus],
    );

    return (
      <ContainerStyled
        ref={ref}
        $conditions={{
          calling: isCalling,
          size,
          color: alpha(
            inviteStatus === 'calling'
              ? theme.palette.text.secondary
              : theme.palette.success.main,
            0.48,
          ),
        }}
      >
        {children}
      </ContainerStyled>
    );
  },
);
